import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiUploadCloud, FiCheck } from 'react-icons/fi';

const Booking = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    window.scrollTo(0, 0);
  };

  if (submitted) {
    return (
      <div className="pt-32 pb-20 bg-background min-h-screen flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center p-12 bg-primary border border-gray-800 max-w-lg mx-auto"
        >
          <div className="w-20 h-20 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheck} className="text-4xl text-secondary" />
          </div>
          <h2 className="text-3xl font-display text-light mb-4">Request Received</h2>
          <p className="text-muted mb-8">
            Thank you for reaching out to Black Anchor. Our artists will review your request and we will be in touch within 2-3 business days to discuss the next steps and set up your consultation.
          </p>
          <button 
            onClick={() => window.location.href = '#/'}
            className="border border-secondary text-secondary px-8 py-3 hover:bg-secondary hover:text-primary transition-colors uppercase tracking-wider text-sm"
          >
            Return Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-20 bg-background min-h-screen">
      <div className="container mx-auto px-6 max-w-3xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-display mb-6">Book Consultation</h1>
          <div className="w-20 h-1 bg-secondary mx-auto mb-6"></div>
          <p className="text-muted">
            Please fill out this form as thoroughly as possible. The more details you provide, the better we can prepare for your project.
          </p>
        </motion.div>

        <motion.form 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="space-y-8 bg-primary p-8 md:p-12 border border-gray-800"
        >
          {/* Personal Info */}
          <div>
            <h3 className="text-xl font-display text-secondary mb-4 border-b border-gray-800 pb-2">1. Personal Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-muted mb-2">Full Name *</label>
                <input required type="text" className="w-full bg-background border border-gray-800 p-3 text-light focus:outline-none focus:border-secondary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted mb-2">Phone Number *</label>
                <input required type="tel" className="w-full bg-background border border-gray-800 p-3 text-light focus:outline-none focus:border-secondary" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-muted mb-2">Email Address *</label>
                <input required type="email" className="w-full bg-background border border-gray-800 p-3 text-light focus:outline-none focus:border-secondary" />
              </div>
            </div>
          </div>

          {/* Project Details */}
          <div>
            <h3 className="text-xl font-display text-secondary mb-4 border-b border-gray-800 pb-2">2. Tattoo Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-muted mb-2">Preferred Artist</label>
                <select className="w-full bg-background border border-gray-800 p-3 text-light focus:outline-none focus:border-secondary appearance-none">
                  <option value="">No Preference / Unsure</option>
                  <option value="alex">Alex Mercer (Blackwork)</option>
                  <option value="sofia">Sofia Reyes (Fine Line)</option>
                  <option value="jordan">Jordan Blake (Realism)</option>
                  <option value="maya">Maya Stone (Traditional)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted mb-2">Tattoo Style</label>
                <select className="w-full bg-background border border-gray-800 p-3 text-light focus:outline-none focus:border-secondary appearance-none">
                  <option value="">Select Style</option>
                  <option value="blackwork">Blackwork</option>
                  <option value="fineline">Fine Line</option>
                  <option value="realism">Realism</option>
                  <option value="traditional">Traditional / Neo-Trad</option>
                  <option value="custom">Custom / Mixed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted mb-2">Estimated Size (inches)</label>
                <input type="text" placeholder="e.g., 4x4" className="w-full bg-background border border-gray-800 p-3 text-light focus:outline-none focus:border-secondary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted mb-2">Placement on Body</label>
                <input type="text" placeholder="e.g., Outer right forearm" className="w-full bg-background border border-gray-800 p-3 text-light focus:outline-none focus:border-secondary" />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-muted mb-2 flex items-center gap-2">
                <input type="checkbox" className="accent-secondary" />
                This is a cover-up of an existing tattoo
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-muted mb-2">Project Description *</label>
              <textarea required rows="4" placeholder="Describe your idea in detail..." className="w-full bg-background border border-gray-800 p-3 text-light focus:outline-none focus:border-secondary resize-none"></textarea>
            </div>
          </div>

          {/* Reference Upload */}
          <div>
            <h3 className="text-xl font-display text-secondary mb-4 border-b border-gray-800 pb-2">3. References</h3>
            <div className="border-2 border-dashed border-gray-800 rounded-sm p-8 text-center hover:border-secondary transition-colors cursor-pointer bg-background">
              <SafeIcon icon={FiUploadCloud} className="text-4xl text-muted mx-auto mb-3" />
              <p className="text-light mb-1">Click to upload reference images</p>
              <p className="text-muted text-xs">PNG, JPG up to 5MB</p>
              <input type="file" className="hidden" multiple />
            </div>
            <p className="text-xs text-muted mt-2 mt-4 text-center">Note: This is a demo form. Uploads are disabled.</p>
          </div>

          <button type="submit" className="w-full bg-secondary text-primary px-8 py-4 font-medium hover:bg-light transition-colors uppercase tracking-wider text-sm mt-8">
            Submit Booking Request
          </button>
        </motion.form>
      </div>
    </div>
  );
};

export default Booking;